import { logError } from "@entrilia/shared/logging";
import { redactSasSignaturesInText } from "@entrilia/shared/utilities/urlTelemetry";
import { wait } from "@entrilia/shared/utilities/promiseHelper";
import { isSasUrlExpired } from "@entrilia/shared/utilities/sasUrl";
import type { FileInfo } from "../../api/types/commonApiTypes";
import type { ConnectionCheckResult, DocumentLoadFailure } from "../../auth/accessCheck/types";
import { probeDocumentUrl } from "../../auth/accessCheck/runConnectionChecks";
import {
  emitPdfBlobCheckTriggered,
  emitPdfRetryAborted,
  emitPdfRetryAttempt,
  emitPdfRetryResolved,
} from "./pdfBlobCheckTelemetry";
import type { RetryAbortReason, RetryOutcomeKind } from "./pdfBlobCheckTelemetry";
import type { PdfBlobCheckResolvedState, PdfRetrySessionPhase } from "./pdfBlobCheckTypes";
import { runPdfBlobCheckDiagnostics } from "./pdfBlobCheckDiagnostics";
import { MAX_RETRY_ATTEMPTS, runDocumentRetrySequence } from "./pdfDocumentRetry";
import type { RetryResult } from "./pdfDocumentRetry";

export type RefreshDocumentUrl = () => Promise<FileInfo | undefined>;

// PDF.js fires load errors repeatedly, so this session also works as the re-entrancy
// guard. "retried" means a probe succeeded; a second failure diagnoses instead of looping.
export interface PdfRetrySession {
  fileSrc: string;
  url: string;
  phase: PdfRetrySessionPhase;
  cancelled: boolean;
  abortTelemetryEmitted: boolean;
  attempts: number;
  urlRefreshed: boolean;
  sasExpired: boolean;
}

interface RetryTelemetry {
  retryAttempts: number;
  retryOutcome: RetryOutcomeKind;
}

interface PdfLoadErrorCallbacks {
  onSessionCreated: (session: PdfRetrySession) => void;
  onRetrying: (attempt: number, maxAttempts: number) => void;
  onChecking: () => void;
  onResolved: (state: PdfBlobCheckResolvedState) => void;
  onRetrySucceeded: (fileInfo: FileInfo | undefined) => void;
}

interface HandlePdfLoadErrorArgs {
  fileSrc: string;
  effectiveUrl: string;
  error?: Error;
  documentViewSessionId?: string;
  session?: PdfRetrySession;
  refreshUrl?: RefreshDocumentUrl;
  callbacks: PdfLoadErrorCallbacks;
}

interface RunFinalDiagnosticsArgs {
  session: PdfRetrySession;
  fileSrc: string;
  documentViewSessionId?: string;
  documentProbe?: ConnectionCheckResult;
  loadFailure?: DocumentLoadFailure;
  retry?: RetryTelemetry;
  callbacks: Pick<PdfLoadErrorCallbacks, "onChecking" | "onResolved">;
}

// PDF.js reports status 0 when none arrived; its messages can quote the signed URL, so redact the SAS here.
export const toDocumentLoadFailure = (error: Error): DocumentLoadFailure => {
  const statusCode =
    "status" in error && typeof error.status === "number" && error.status > 0 ? error.status : undefined;
  return { errorName: error.name, errorMessage: redactSasSignaturesInText(error.message), statusCode };
};

export const isPdfRetryActive = (session: PdfRetrySession): boolean =>
  session.phase === "retrying" || session.phase === "diagnosing";

const emitRetryAbortedOnce = (
  session: PdfRetrySession,
  documentViewSessionId: string | undefined,
  reason: RetryAbortReason
): void => {
  if (session.abortTelemetryEmitted) {
    return;
  }

  session.abortTelemetryEmitted = true;
  emitPdfRetryAborted({
    fileSrc: session.fileSrc,
    documentViewSessionId,
    attempts: session.attempts,
    maxAttempts: MAX_RETRY_ATTEMPTS,
    phase: session.phase,
    reason,
    urlRefreshed: session.urlRefreshed,
  });
};

export const abortPdfRetrySession = (
  session: PdfRetrySession,
  documentViewSessionId: string | undefined,
  reason: RetryAbortReason
): void => {
  session.cancelled = true;
  emitRetryAbortedOnce(session, documentViewSessionId, reason);
};

const createPdfRetrySession = (fileSrc: string): PdfRetrySession => ({
  fileSrc,
  url: fileSrc,
  phase: "retrying",
  cancelled: false,
  abortTelemetryEmitted: false,
  attempts: 0,
  urlRefreshed: false,
  sasExpired: isSasUrlExpired(fileSrc),
});

const runFinalDiagnostics = async ({
  session,
  fileSrc,
  documentViewSessionId,
  documentProbe,
  loadFailure,
  retry,
  callbacks,
}: RunFinalDiagnosticsArgs): Promise<void> => {
  session.phase = "diagnosing";
  callbacks.onChecking();
  const resolved = await runPdfBlobCheckDiagnostics({
    fileSrc,
    documentViewSessionId,
    documentProbe,
    loadFailure,
    retry,
  });
  session.phase = "diagnosed";
  if (!session.cancelled) {
    callbacks.onResolved(resolved);
  }
};

export const handlePdfLoadError = async ({
  fileSrc,
  effectiveUrl,
  error,
  documentViewSessionId,
  session,
  refreshUrl,
  callbacks,
}: HandlePdfLoadErrorArgs): Promise<void> => {
  if (fileSrc !== effectiveUrl) {
    // Late error from a Document being replaced after a URL refresh.
    return;
  }
  if (session && session.phase !== "retried") {
    // Active or completed diagnostics already own this load-error cycle.
    return;
  }

  const loadFailure = error ? toDocumentLoadFailure(error) : undefined;
  emitPdfBlobCheckTriggered({ fileSrc, documentViewSessionId });

  if (session) {
    await runFinalDiagnostics({ session, fileSrc, documentViewSessionId, loadFailure, callbacks });
    return;
  }

  const newSession = createPdfRetrySession(fileSrc);
  callbacks.onSessionCreated(newSession);
  callbacks.onRetrying(1, MAX_RETRY_ATTEMPTS);

  let result: RetryResult;
  try {
    result = await runDocumentRetrySequence(fileSrc, {
      probe: probeDocumentUrl,
      waitFn: wait,
      isSasExpired: isSasUrlExpired,
      refreshUrl,
      isCancelled: () => newSession.cancelled,
      onStateChange: ({ url: retryUrl, refreshed, sasExpired }) => {
        newSession.url = retryUrl;
        newSession.urlRefreshed = refreshed;
        newSession.sasExpired = sasExpired;
      },
      onAttempt: (attempt, urlRefreshed) => {
        newSession.attempts = attempt;
        newSession.urlRefreshed = urlRefreshed;
        if (!newSession.cancelled) {
          callbacks.onRetrying(attempt, MAX_RETRY_ATTEMPTS);
        }
        emitPdfRetryAttempt({ fileSrc, documentViewSessionId, attempt, maxAttempts: MAX_RETRY_ATTEMPTS, urlRefreshed });
      },
    });
  } catch (error: unknown) {
    logError(error, "[pdfLoadErrorHandler] runDocumentRetrySequence");
    emitPdfRetryResolved({
      fileSrc,
      documentViewSessionId,
      attempts: newSession.attempts,
      outcome: "error",
      sasExpired: newSession.sasExpired,
      urlRefreshed: newSession.urlRefreshed,
    });
    await runFinalDiagnostics({
      session: newSession,
      fileSrc: newSession.url,
      documentViewSessionId,
      loadFailure,
      retry: { retryAttempts: newSession.attempts, retryOutcome: "error" },
      callbacks,
    });
    return;
  }

  const resolvedProps = {
    fileSrc,
    documentViewSessionId,
    attempts: result.attempts,
    sasExpired: result.sasExpired,
    urlRefreshed: result.refreshed,
  };

  if (result.outcome === "cancelled" || newSession.cancelled) {
    emitPdfRetryResolved({ ...resolvedProps, outcome: "cancelled" });
    return;
  }

  if (result.outcome === "probe-ok") {
    newSession.phase = "retried";
    newSession.url = result.url;
    newSession.urlRefreshed = result.refreshed;
    callbacks.onRetrySucceeded(result.fileInfo);
    emitPdfRetryResolved({ ...resolvedProps, outcome: result.refreshed ? "refreshed-ok" : "retried-ok" });
    return;
  }

  emitPdfRetryResolved({ ...resolvedProps, outcome: "exhausted" });
  await runFinalDiagnostics({
    session: newSession,
    fileSrc: result.url,
    documentViewSessionId,
    documentProbe: result.lastFailedProbe,
    loadFailure,
    retry: { retryAttempts: result.attempts, retryOutcome: "exhausted" },
    callbacks,
  });
};
